import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
} from '@mui/material';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import { PermanentInjury, WarbandMember } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

function formatInjuryEffect(injury: PermanentInjury): string {
  const parts: string[] = [];
  if (injury.fightChange != null) parts.push(`Fight ${injury.fightChange > 0 ? '+' : ''}${injury.fightChange}`);
  if (injury.shootChange != null) parts.push(`Shoot ${injury.shootChange > 0 ? '+' : ''}${injury.shootChange}`);
  if (injury.moveChange != null) parts.push(`Move ${injury.moveChange > 0 ? '+' : ''}${injury.moveChange}`);
  if (injury.willChange != null) parts.push(`Will ${injury.willChange > 0 ? '+' : ''}${injury.willChange}`);
  if (injury.healthChange != null) parts.push(`Health ${injury.healthChange > 0 ? '+' : ''}${injury.healthChange}`);
  if (injury.castingNumberChange != null) parts.push(`Casting Numbers ${injury.castingNumberChange > 0 ? '+' : ''}${injury.castingNumberChange}`);
  return parts.length > 0 ? parts.join(', ') : 'No stat effect';
}

interface Props {
  open: boolean;
  member: WarbandMember | null;
  onClose: () => void;
  onSelect: (injury: PermanentInjury) => void;
}

export const SelectPermanentInjuryModal: React.FC<Props> = ({ open, member, onClose, onSelect }) => {
  const injuries = useReferenceDataStore((s) => s.injuries);
  const [selected, setSelected] = React.useState<PermanentInjury | null>(null);

  React.useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
      setSelected(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonalInjuryIcon />
        Permanent Injury — {member?.name ?? ''}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select the injury suffered by this spellcaster.
        </Typography>
        <List disablePadding>
          {injuries.map((injury) => (
            <ListItemButton
              key={injury.id}
              selected={selected?.id === injury.id}
              onClick={() => setSelected(injury)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body1" fontWeight={selected?.id === injury.id ? 600 : 400}>
                    {injury.name}
                  </Typography>
                }
                secondary={
                  <>
                    <Chip
                      label={formatInjuryEffect(injury)}
                      size="small"
                      color={injury.fightChange != null || injury.shootChange != null || injury.moveChange != null || injury.willChange != null || injury.healthChange != null || injury.castingNumberChange != null ? 'error' : 'default'}
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary" component="span">
                      {injury.description}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!selected}
          onClick={handleConfirm}
          startIcon={<PersonalInjuryIcon />}
        >
          Apply Injury
        </Button>
      </DialogActions>
    </Dialog>
  );
};
